import { AccountingEventsGroup } from "../types/events";
import { HealthState, HealthStatus } from "../types/health";
import { DlqInfo } from "../hooks/useFetchDlqInfo";
import { CanonicalBusinessEvent } from "../types/cbes";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../types/documents";
import { isDigitalDocument, isDocumentFlowCbe } from "./typeChecks";
import {
  shouldHideAccountingFlow,
  shouldHideDocumentFlow,
} from "./flowTypeUtils";
import { filterNonNoOpCBEs, isNoOpCBE } from "./cbeUtils";

export function groupCBEsByVersion(cbes: CanonicalBusinessEvent[]) {
  return cbes.reduce((acc, cbe) => {
    const version = cbe.lockVersion;
    acc[version] = acc[version] || [];
    acc[version].push(cbe);
    return acc;
  }, {} as Record<number, CanonicalBusinessEvent[]>);
}

export const createVersionNumberArray = (
  cbeData: CanonicalBusinessEvent[]
): number[] =>
  Array.from(new Set(cbeData.map((cbe) => cbe.lockVersion))).sort(
    (a, b) => b - a
  );

// Helper function to get the latest version CBEs for each flow separately
function getLatestVersionCBEsByFlow(cbes: CanonicalBusinessEvent[]): {
  accounting: CanonicalBusinessEvent[];
  document: CanonicalBusinessEvent[];
} {
  if (!cbes.length) return { accounting: [], document: [] };

  const documentCBEs = cbes.filter((cbe) => isDocumentFlowCbe(cbe));
  const accountingCBEs = cbes.filter((cbe) => !isDocumentFlowCbe(cbe));

  // Get the latest version for each flow
  const documentGrouped = groupCBEsByVersion(documentCBEs);
  const accountingGrouped = groupCBEsByVersion(accountingCBEs);

  const documentVersions = createVersionNumberArray(documentCBEs);
  const accountingVersions = createVersionNumberArray(accountingCBEs);

  const latestDocumentVersion =
    documentVersions.length > 0 ? documentVersions[0] : -1;
  const latestAccountingVersion =
    accountingVersions.length > 0 ? accountingVersions[0] : -1;

  return {
    document:
      latestDocumentVersion >= 0
        ? documentGrouped[latestDocumentVersion] || []
        : [],
    accounting:
      latestAccountingVersion >= 0
        ? accountingGrouped[latestAccountingVersion] || []
        : [],
  };
}

export function getCBEPublishStatus(cbe: CanonicalBusinessEvent) {
  const isNoOp = isNoOpCBE(cbe);

  return {
    isSuccess: cbe.publishStatus?.successFlag === true || isNoOp,
    isFailed: cbe.publishStatus?.successFlag === false && !isNoOp,
    isUnpublished: cbe.publishStatus === undefined && !isNoOp,
    isNoOp,
  };
}

export function determineCbeGroupHealth(
  cbes: CanonicalBusinessEvent[]
): HealthStatus {
  if (!cbes || cbes.length === 0) return "pending";

  const meaningfulCBEs = filterNonNoOpCBEs(cbes);
  if (meaningfulCBEs.length === 0) return "pending";

  const publishStatuses = meaningfulCBEs.map(getCBEPublishStatus);

  if (publishStatuses.some((status) => status.isFailed)) return "unhealthy";
  if (publishStatuses.some((status) => status.isUnpublished)) return "pending";
  if (publishStatuses.every((status) => status.isSuccess)) return "healthy";

  return "unknown";
}

export function determineOverallHealth(
  eventsReceived: boolean,
  latestCBEs: CanonicalBusinessEvent[],
  dlqInfo?: DlqInfo,
  isFreeTrial = false
): HealthStatus {
  // Check for NoOp CBEs
  const noOpCBEs = latestCBEs.filter(isNoOpCBE);
  const hasOnlyNoOpCBEs =
    noOpCBEs.length === latestCBEs.length && noOpCBEs.length > 0;

  if (dlqInfo?.status === "loading") {
    if (!eventsReceived) {
      return "unhealthy";
    }
    return "unknown";
  }

  if (dlqInfo?.status === "error") {
    return "unknown";
  }

  // Check for DLQ items - if there are any, the status is always unhealthy
  const hasDlqItems = dlqInfo?.dlqItems && dlqInfo.dlqItems.length > 0;
  if (hasDlqItems) {
    return "unhealthy";
  }

  // 1. No events means the order is not being processed at all
  if (!eventsReceived) return "unhealthy";

  // 2. If we have no CBEs (which can happen when all flows are hidden or
  // when we have events but no CBEs have been produced yet), we need to handle this case
  if (latestCBEs.length === 0) {
    // If we have events but no CBEs, it means the processing is still in progress
    return "pending";
  }

  // If all CBEs are NoOp, consider it pending UNLESS it's a free trial
  if (hasOnlyNoOpCBEs) {
    // For free trials with NoOp CBEs, consider it healthy
    if (isFreeTrial) {
      return "healthy";
    }
    // For regular orders with NoOp CBEs, consider it pending
    return "pending";
  }

  // 3. Check if all CBEs are successful
  const cbeStatuses = latestCBEs.map(getCBEPublishStatus);

  const allCbesSuccessful = cbeStatuses.every((status) => status.isSuccess);
  if (allCbesSuccessful) {
    return "healthy";
  }

  // 4. Check if any CBE has failed
  const anyFailed = cbeStatuses.some((status) => status.isFailed);
  if (anyFailed) {
    return "unhealthy";
  }

  // 5. If we have CBEs but none are failed and not all are successful,
  // then some must be unpublished, so the status is pending
  return "pending";
}

export function calculateCompleteHealthState(
  events: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
  )[],
  cbes: CanonicalBusinessEvent[],
  dlqInfo: DlqInfo,
  glProduct?: string,
  isFreeTrial = false
): HealthState {
  const hasEvents = events.length > 0;

  const latestCBEsByFlow = getLatestVersionCBEsByFlow(cbes);

  const hasDocumentEvents = events.some((event) => isDigitalDocument(event));
  const hasAccountingEvents = events.some((event) => !isDigitalDocument(event));

  const hideAccountingFlow = shouldHideAccountingFlow(events, cbes);
  const hideDocumentFlow = shouldHideDocumentFlow(events);

  // For overall health, only include flows that should be visible
  let relevantCBEs: CanonicalBusinessEvent[] = [];

  if (!hideAccountingFlow) {
    relevantCBEs = [...relevantCBEs, ...latestCBEsByFlow.accounting];
  }

  if (!hideDocumentFlow) {
    relevantCBEs = [...relevantCBEs, ...latestCBEsByFlow.document];
  }

  // Calculate overall health status using only the visible flows
  const overallHealthStatus = determineOverallHealth(
    hasEvents,
    relevantCBEs,
    dlqInfo,
    isFreeTrial
  );

  // Check for NoOp CBEs in relevant flows
  const relevantNoOpCBEs = relevantCBEs.filter(isNoOpCBE);
  const hasOnlyRelevantNoOpCBEs =
    relevantNoOpCBEs.length === relevantCBEs.length &&
    relevantNoOpCBEs.length > 0;

  const accountingCBEs = latestCBEsByFlow.accounting;
  const hasAccountingCBEs = accountingCBEs.length > 0;

  // Check for NoOp CBEs in accounting flow
  const accountingNoOpCBEs = accountingCBEs.filter(isNoOpCBE);
  const hasOnlyAccountingNoOpCBEs =
    accountingNoOpCBEs.length === accountingCBEs.length &&
    accountingNoOpCBEs.length > 0;

  const accountingCBEsPublished =
    hasAccountingCBEs &&
    (hasOnlyAccountingNoOpCBEs ||
      accountingCBEs.every((cbe) => getCBEPublishStatus(cbe).isSuccess));

  const documentCBEs = latestCBEsByFlow.document;
  const hasDocumentCBEs = documentCBEs.length > 0;

  // Check for NoOp CBEs in document flow
  const documentNoOpCBEs = documentCBEs.filter(isNoOpCBE);
  const hasOnlyDocumentNoOpCBEs =
    documentNoOpCBEs.length === documentCBEs.length &&
    documentNoOpCBEs.length > 0;

  const documentCBEsPublished =
    hasDocumentCBEs &&
    (hasOnlyDocumentNoOpCBEs ||
      documentCBEs.every((cbe) => getCBEPublishStatus(cbe).isSuccess));

  // Check if any visible flow has failed CBEs
  const hasFailedCBEs = relevantCBEs.some((cbe) => {
    const status = getCBEPublishStatus(cbe);
    return status.isFailed;
  });

  // Determine overall CBE status
  const cbesProduced = relevantCBEs.length > 0;

  const allCbesSuccessful =
    cbesProduced &&
    (hasOnlyRelevantNoOpCBEs ||
      relevantCBEs.every((cbe) => getCBEPublishStatus(cbe).isSuccess));

  const healthState = {
    overallHealth: hasFailedCBEs ? "unhealthy" : overallHealthStatus,
    eventsReceived: hasEvents,
    cbeStatus: {
      produced: cbesProduced,
      published: allCbesSuccessful || (hasOnlyRelevantNoOpCBEs && isFreeTrial),
    },
    accountingFlow: {
      eventsReceived: hasAccountingEvents,
      cbesProduced: hasAccountingCBEs,
      cbesPublished:
        accountingCBEsPublished || (hasOnlyAccountingNoOpCBEs && isFreeTrial),
      hidden: hideAccountingFlow,
      hasOnlyNoOpCbes: hasOnlyAccountingNoOpCBEs,
    },
    documentFlow: {
      eventsReceived: hasDocumentEvents,
      cbesProduced: hasDocumentCBEs,
      cbesPublished:
        documentCBEsPublished || (hasOnlyDocumentNoOpCBEs && isFreeTrial),
      hidden: hideDocumentFlow,
      hasOnlyNoOpCbes: hasOnlyDocumentNoOpCBEs,
    },
    hasOnlyNoOpCbes: hasOnlyRelevantNoOpCBEs,
    isFreeTrial,
  };

  return healthState;
}
